import React from "react";
import { ArrowLeft, Ban, Flag, MessageCircle, Send, User } from "lucide-react";
import { timeAgo } from "@/lib/api";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";

/**
 * Message thread: conversation header, message list and the send box.
 *
 * Extracted verbatim from src/pages/Messages.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged.
 */
export default function MessageThread({
  activeId,
  bottomRef,
  loadingThread,
  messages,
  navigate,
  other,
  sending,
  send,
  setBlockOpen,
  setParams,
  setReportOpen,
  setText,
  text,
  thread,
  user,
  hasUnlimited,
}) {
  return (
        <section className={`sm-card bg-white flex flex-col min-h-[60vh] ${activeId ? "" : "hidden lg:flex"}`}>
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <MessageCircle className="w-10 h-10 mx-auto text-[#BFD9DB]" />
                <p className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">
                  Choose a conversation
                </p>
                <p className="mt-1 text-sm text-[#4B5A60]">
                  Your private conversations appear here.
                </p>
              </div>
            </div>
          ) : loadingThread && messages.length === 0 ? (
            <Spinner label="Loading conversation" />
          ) : (
            <>
              <header className="px-4 py-3 border-b border-[#E3DED4] bg-[#F7F4EF] flex items-center gap-3">
                <button
                  onClick={() => setParams({})}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-[#E9E4DA]"
                  aria-label="Back"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <MemberAvatar user={other} size={40} />
                <div className="min-w-0 flex-1">
                  <p data-testid="thread-other-name" className="font-bold text-sm text-[#1F2A2E] truncate">
                    {other?.display_name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#4B5A60]">
                    {thread?.other_is_sponsor && thread?.sponsor_program ? (
                      <ProgramBadge program={thread.sponsor_program} />
                    ) : null}
                    <span>
                      {other?.presence === "online"
                        ? "Online now"
                        : other?.presence === "recently_active"
                        ? "Active recently"
                        : "Offline"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {thread?.other_is_sponsor && (
                    <button
                      onClick={() => navigate(`/sponsors/${other.id}`)}
                      data-testid="thread-view-profile"
                      className="p-2 rounded-lg hover:bg-[#E9E4DA]"
                      title="Sponsor profile"
                    >
                      <User className="w-4 h-4 text-[#4B5A60]" />
                    </button>
                  )}
                  <button
                    onClick={() => setReportOpen(true)}
                    data-testid="thread-report-button"
                    className="p-2 rounded-lg hover:bg-[#FBE6E4]"
                    title="Report"
                  >
                    <Flag className="w-4 h-4 text-[#B42318]" />
                  </button>
                  <button
                    onClick={() => setBlockOpen(true)}
                    data-testid="thread-block-button"
                    className="p-2 rounded-lg hover:bg-[#FBE6E4]"
                    title="Block"
                  >
                    <Ban className="w-4 h-4 text-[#B42318]" />
                  </button>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto sm-scroll px-4 py-5 space-y-3 bg-[#FBFAF7] max-h-[55vh]">
                {messages.length === 0 ? (
                  <p className="text-center text-sm text-[#7A868B] py-8">
                    No messages yet. Say hello when you are ready.
                  </p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div
                        key={m.id}
                        data-testid={`message-${m.id}`}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-6 ${
                            mine
                              ? "bg-[#1F6F78] text-white rounded-br-md"
                              : "bg-white text-[#1F2A2E] border border-[#E3DED4] rounded-bl-md"
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{m.message_text}</p>
                          <p
                            className={`mt-1 text-[10px] ${
                              mine ? "text-[#BFE0E3]" : "text-[#7A868B]"
                            }`}
                          >
                            {timeAgo(m.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {thread?.is_blocked ? (
                <div className="px-4 py-4 border-t border-[#E3DED4] bg-[#FBE6E4] text-sm text-[#B42318]">
                  This conversation is blocked. Unblock from your settings to message again.
                </div>
              ) : (
                <form onSubmit={send} className="px-4 py-3 border-t border-[#E3DED4] bg-white">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send(e);
                        }
                      }}
                      rows={1}
                      data-testid="message-input"
                      placeholder="Type a message..."
                      className="flex-1 resize-none rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm text-[#1F2A2E] focus:outline-none focus:ring-2 focus:ring-[#2A8C97] max-h-32"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !text.trim()}
                      data-testid="send-message-submit"
                      className="h-11 bg-[#1F6F78] hover:bg-[#185B62] text-white"
                    >
                      <Send className="w-4 h-4" />
                      <span className="ml-1.5 hidden sm:inline">Send</span>
                    </Button>
                  </div>
                  {!hasUnlimited && (
                    <p className="mt-2 text-xs text-[#4B5A60]">
                      Messages you receive never count against your free balance.
                    </p>
                  )}
                </form>
              )}
            </>
          )}
        </section>
  );
}
